/**
 * Opleiders-cockpit schil: ZijMenu (organisatie + rol + submenu's van het
 * actieve hoofdmenu) met de paginatitel in de inhoudskolom, en de client-side Rechten-Grendel:
 * een pad dat de rol niet mag zien stuurt door naar het eerste toegestane pad.
 * De server blokkeert dezelfde endpoints (vereisOpleiderRol).
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop, type ZijMenuItem } from '../ZijMenu';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { hoofdmenuVoorPad, hoofdmenusVoor, magRol, startPadVoor, subItemVoorPad } from './opleiderMenu';
import { ROL_KLEUR, useOpleiderRol } from './useOpleiderRol';
import { uploadUrl } from '../../utils/uploadUrl';

export const OpleiderCockpitLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const { user, updateUser } = useAuthStore();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isOpleider = String(user?.role) === 'opleider';

  // Rol + organisatie komen van /auth/me (hoofdeigenaar of teamlid, migratie 150)
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me').then((res) => res.data.data),
    enabled: isOpleider,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (me.data?.user) updateUser(me.data.user);
  }, [me.data?.user, updateUser]);

  const rol = useOpleiderRol();
  if (!isOpleider) return <Navigate to="/" replace />;
  if (!rol) {
    if (me.isLoading || !me.data) {
      return <div className="min-h-[40vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1F3A]" /></div>;
    }
    return <Navigate to="/opleider/onboarding" replace />;
  }

  const gevonden = subItemVoorPad(location.pathname);
  if (gevonden && !magRol(gevonden.item.rollen, rol)) return <Navigate to={startPadVoor(rol)} replace />;
  const menu = hoofdmenuVoorPad(location.pathname) ?? hoofdmenusVoor(rol)[0];
  if (menu && !magRol(menu.rollen, rol)) return <Navigate to={startPadVoor(rol)} replace />;

  const organisatie = (user as { opleider_organisatie?: string | null } | null)?.opleider_organisatie ?? me.data?.profile?.organisatie_naam ?? '';
  const titel = gevonden ? t(`opleiderNav.sub.${gevonden.item.key}`) : menu ? t(`opleiderNav.${menu.key}`) : '';
  const logo = me.data?.profile?.logo_url as string | undefined;
  const logoUrl = logo ? uploadUrl(logo) : null;

  const actiefItem = gevonden?.item.key;
  const items: ZijMenuItem[] = menu
    ? menu.items.filter((i) => magRol(i.rollen, rol)).map((item) => ({
        key: item.key,
        label: t(`opleiderNav.sub.${item.key}`),
        icon: item.icon,
        href: item.pad,
        actief: actiefItem === item.key,
      }))
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <ZijMenuMobielKnop onClick={() => setOpen(true)} label={t('opleiderNav.menu', 'Menu')} />
      <div className="max-w-7xl mx-auto flex h-[calc(100vh-4rem)] overflow-hidden">
        {menu && (
          <ZijMenu
            kop={
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-navy-50 border border-navy-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-contain" /> : <img src="/belagom-symbol.svg" alt="BeLagom" className="w-full h-full object-contain p-1.5" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy-900 leading-tight truncate">{organisatie || user?.first_name}</p>
                  <span className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ROL_KLEUR[rol]}`}>
                    <ShieldCheck className="w-3 h-3" />
                    {t(`opleiderNav.rol.${rol}`)}
                  </span>
                </div>
              </div>
            }
            titel={t(`opleiderNav.${menu.key}`)}
            ondertitel={t(`opleiderNav.subtitle.${menu.key}`)}
            groepen={[{ items }]}
            mobielOpen={open}
            onMobielSluit={() => setOpen(false)}
          />
        )}
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">
          <PaginaKop titel={titel} ondertitel={t('opleiderNav.welcome', { name: user?.first_name, organisatie })} className="px-4 sm:px-6 pt-6" />
          {children}
        </div>
      </div>
    </div>
  );
};
